/**
 * Os nós do editor, como eles aparecem para quem usa.
 *
 * Fonte única de `tipo interno → nome de tela`. A biblioteca do editor lê daqui
 * e o Lucas também, porque o problema que originou este arquivo foi exatamente
 * as duas pontas conhecerem vocabulários diferentes: ele mandava "adicione um
 * nó **input**" e na tela está escrito "Aguardar Resposta".
 *
 * Não era alucinação. A única fonte de nome que o modelo recebia era o
 * documento de referência da engine, indexado por executor (`## Executor:
 * input`) — o vocabulário do código. Ele repetiu o que lhe deram.
 *
 * `icon` é o NOME do ícone, não o componente: este módulo é lido pela API, que
 * não tem React. `color` é o token base (`cyan`), e cada lado monta a classe
 * que precisa — a paleta usa `text-cyan-400`, o cartão usa fundo e borda.
 */

export const NODE_CATALOG = Object.freeze([
  // ── Básico ──────────────────────────────────────────────────────────────
  { type: "start", label: "Início", icon: "Flag", color: "emerald", category: "Básico" },
  { type: "call_start", label: "Chamada WhatsApp", icon: "Phone", color: "sky", category: "Básico" },
  { type: "webhook_start", label: "Webhook Trigger", icon: "Webhook", color: "violet", category: "Básico" },
  { type: "cron_trigger", label: "Trigger Agendado", icon: "CalendarClock", color: "pink", category: "Básico" },
  { type: "end", label: "Fim", icon: "CheckCircle2", color: "gray", category: "Básico" },
  { type: "note", label: "Nota/Obs", icon: "FileText", color: "yellow", category: "Básico" },

  // ── Lógica & Controle ───────────────────────────────────────────────────
  { type: "condition", label: "Condição (If/Else)", icon: "Split", color: "indigo", category: "Lógica & Controle" },
  { type: "branch", label: "Branch (Switch)", icon: "GitBranch", color: "violet", category: "Lógica & Controle" },
  { type: "round_robin", label: "Rodízio (Round Robin)", icon: "Repeat", color: "teal", category: "Lógica & Controle" },
  { type: "schedule", label: "Horário", icon: "CalendarClock", color: "pink", category: "Lógica & Controle" },
  { type: "timeout", label: "Aguardar (Delay)", icon: "Clock", color: "amber", category: "Lógica & Controle" },
  { type: "input", label: "Aguardar Resposta", icon: "MousePointerClick", color: "cyan", category: "Lógica & Controle" },
  { type: "jump", label: "Jump To (Ir Para)", icon: "ArrowUpRight", color: "orange", category: "Lógica & Controle" },
  { type: "set_variable", label: "Definir Variável", icon: "Braces", color: "teal", category: "Lógica & Controle" },
  { type: "script", label: "Script JS", icon: "Code2", color: "cyan", category: "Lógica & Controle" },

  // ── Inteligência ────────────────────────────────────────────────────────
  { type: "ai_agent", label: "Agente IA", icon: "Bot", color: "purple", category: "Inteligência" },
  { type: "classifier", label: "Classificador", icon: "Split", color: "fuchsia", category: "Inteligência" },
  { type: "extractor", label: "Extrator", icon: "Braces", color: "pink", category: "Inteligência" },
  { type: "speech_to_text", label: "Voz → Texto", icon: "MessageSquare", color: "violet", category: "Inteligência" },
  { type: "text_to_speech", label: "Texto → Voz", icon: "MessageSquare", color: "indigo", category: "Inteligência" },
  { type: "image_analysis", label: "Análise de Imagem", icon: "Bot", color: "cyan", category: "Inteligência" },
  { type: "summarizer", label: "Sumarizador", icon: "FileText", color: "teal", category: "Inteligência" },

  // ── Ações & Integrações ─────────────────────────────────────────────────
  { type: "whatsapp", label: "Enviar WhatsApp", icon: "MessageSquare", color: "green", category: "Ações & Integrações" },
  { type: "contact_update", label: "Editar Contato", icon: "UserPen", color: "sky", category: "Ações & Integrações" },
  { type: "crm", label: "CRM", icon: "Briefcase", color: "blue", category: "Ações & Integrações" },
  { type: "projuris_action", label: "Projuris", icon: "ProjurisIcon", color: "blue", category: "Ações & Integrações" },
  { type: "controljus_action", label: "ControlJUS", icon: "ControlJusIcon", color: "indigo", category: "Ações & Integrações" },
  { type: "advbox_action", label: "AdvBox", icon: "AdvBoxIcon", color: "sky", category: "Ações & Integrações" },
  { type: "astrea_action", label: "Astrea", icon: "AstreaIcon", color: "blue", category: "Ações & Integrações" },
  { type: "faz_action", label: "SEDEP FAZ", icon: "FazIcon", color: "blue", category: "Ações & Integrações" },
  { type: "pipedrive_action", label: "Pipedrive", icon: "Briefcase", color: "emerald", category: "Ações & Integrações" },
  { type: "trello_action", label: "Trello", icon: "TrelloIcon", color: "sky", category: "Ações & Integrações" },
  { type: "transfer", label: "Transferência", icon: "PhoneForwarded", color: "amber", category: "Ações & Integrações" },
  { type: "mcp", label: "MCP (Ferramentas)", icon: "Zap", color: "amber", category: "Ações & Integrações" },
  { type: "zapsign", label: "Gerar Contrato", icon: "Zap", color: "orange", category: "Ações & Integrações" },
  { type: "email", label: "Enviar E-mail", icon: "Mail", color: "sky", category: "Ações & Integrações" },
  { type: "payment", label: "Cobrança", icon: "CreditCard", color: "blue", category: "Ações & Integrações" },
  { type: "fetch", label: "Requisição HTTP", icon: "Globe", color: "orange", category: "Ações & Integrações" },
  { type: "survey", label: "Pesquisa (NPS/CSAT)", icon: "Star", color: "amber", category: "Ações & Integrações" },
]);

const BY_TYPE = new Map(NODE_CATALOG.map((node) => [node.type, node]));

/**
 * O nome que a pessoa vê na biblioteca.
 *
 * Tipo desconhecido devolve o próprio tipo: um nó recém-criado no editor e
 * ainda não catalogado deixa a frase feia, mas inteira — melhor que uma
 * instrução com buraco no meio.
 *
 * @param {string} type
 * @returns {string}
 */
export function nodeLabel(type) {
  if (!type) return "";
  return BY_TYPE.get(type)?.label || type;
}

/**
 * @param {string} type
 * @returns {{type: string, label: string, icon: string, color: string, category: string}|null}
 */
export function nodeByType(type) {
  return BY_TYPE.get(type) || null;
}

/** Uma linha por nó, para injetar no prompt: `input = Aguardar Resposta`. */
export function labelledTypes() {
  return NODE_CATALOG.map((n) => `${n.type} = ${n.label}`);
}

export default NODE_CATALOG;
